// [claude-code 2026-04-28] T6: Inline Fuse replaced with NothingFuse for visual consistency.
// [claude-code 2026-04-19] S25-T4a: Econ Intelligence KPI fuses (left column of Econ page header). Vertically stacked, clear headers, Doto numbers. Aggregates the same /api/data/econ-calendar payload Sanctum already pulls.
// [claude-code 2026-05-03] S57: expanded Econ Pulse renders three fuses side-by-side.
import { useMemo } from "react";
import type { EconEventCardData } from "./EconEventCard";
import { NothingFuse } from "../../shared/NothingFuse";

interface EconKpiFusesProps {
  /** Catalogue of events with releasesCollected populated; same shape as EconEventFilter consumes */
  catalogue: EconEventCardData[];
  /** Latest IV roll-up averaged from prints — optional */
  inflationPulse?: number;
  laborPulse?: number;
  supplyPulse?: number;
}

export function EconKpiFuses({
  catalogue,
  inflationPulse,
  laborPulse,
  supplyPulse,
}: EconKpiFusesProps) {
  // Coverage = how many events in each category have prints collected
  const coverage = useMemo(() => {
    const c = { "price-stability": 0, employment: 0, "supply-chain": 0 };
    for (const evt of catalogue) {
      if (
        evt.releasesCollected > 0 &&
        c[evt.category as keyof typeof c] != null
      ) {
        c[evt.category as keyof typeof c] += 1;
      }
    }
    return c;
  }, [catalogue]);

  return (
    <div className="flex-1 min-w-0 flex flex-col gap-3 px-4 py-3">
      <div className="flex items-baseline justify-between">
        <span
          className="text-[10px] tracking-[0.22em] uppercase text-[var(--fintheon-accent)]/85"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          Econ Pulse
        </span>
        <span className="text-[8px] uppercase tracking-[0.18em] text-[var(--fintheon-muted)]/40">
          IV-weighted, 30d
        </span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <Fuse
          label="Inflation Pulse"
          value={inflationPulse}
          coverage={coverage["price-stability"]}
        />
        <Fuse
          label="Labor Pulse"
          value={laborPulse}
          coverage={coverage["employment"]}
        />
        <Fuse
          label="Supply / Output"
          value={supplyPulse}
          coverage={coverage["supply-chain"]}
        />
      </div>
    </div>
  );
}

function Fuse({
  label,
  value,
  coverage,
}: {
  label: string;
  value?: number;
  coverage?: number;
}) {
  const score = value != null ? Math.max(0, Math.min(10, value)) : 0;
  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between">
        <span
          className="text-[9px] tracking-[0.2em] uppercase text-[var(--fintheon-muted)]/60"
          style={{ fontFamily: "var(--font-heading)" }}
        >
          {label}
        </span>
        <div className="flex items-baseline gap-2">
          {coverage != null && (
            <span className="text-[8px] text-[var(--fintheon-muted)]/35">
              {coverage} src
            </span>
          )}
          <span
            className="text-[14px] font-bold"
            style={{
              fontFamily: "Doto, ui-monospace, monospace",
              letterSpacing: "0.02em",
              color:
                value == null
                  ? "var(--fintheon-muted)"
                  : value >= 7
                    ? "var(--fintheon-severe)"
                    : value >= 5
                      ? "var(--fintheon-neutral-severe)"
                      : value >= 3
                        ? "var(--fintheon-accent)"
                        : "var(--fintheon-low)",
            }}
          >
            {value != null ? value.toFixed(1) : "—"}
          </span>
        </div>
      </div>
      <NothingFuse
        value={score / 10}
        score={score}
        orientation="horizontal"
        thickness={3}
        segments={10}
        animateIn
      />
    </div>
  );
}
